import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import type {
  ISitemapNode,
  ISitemapNodeUpdate,
  ISitemapNodeVariant,
  ISitemapNodeVariantUpdate,
  ISitemapTree,
  TSitemapTreeType,
} from '@informally/common/sitemaps';
import { DashboardService } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/dashboards/services/dashboards.service';
import type { IConfirmDialogData } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/sitemap/interfaces/confirm-dialog-data.interface';
import { slugify } from '@suvo/shared/strings';
import type { Client, FromAPI } from '@suvo/shared/interface-utils';
import { lastValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';

interface ISitemapDeletionNode {
  _id: string;
  children?: ISitemapDeletionNode[];
}

@Injectable({ providedIn: 'root' })
export class SitemapEditingService {
  constructor(
    private readonly dashboardService: DashboardService,
    private readonly httpClient: HttpClient,
    private readonly matDialog: MatDialog,
  ) {
    this.loadSitemaps();
  }

  /** @deprecated Need to stop using full sitemap trees. */
  internalSitemaps: ISitemapTree<FromAPI>[];

  /** @deprecated Need to stop using full sitemap trees. */
  publicSitemap: ISitemapTree<FromAPI>;

  /** @deprecated Need to stop using full sitemap trees. */
  privateSitemap: ISitemapTree<FromAPI>;

  /** @deprecated Need to stop using full sitemap trees. */
  readonly sitemaps = signal<ISitemapTree<FromAPI>[]>(null);

  /** @deprecated Need to stop using full sitemap trees. */
  sitemapsPromise: Promise<ISitemapTree<FromAPI>[]>;

  /** @deprecated Need to stop using full sitemap trees. */
  loadSitemaps() {
    return (this.sitemapsPromise = Promise.all([
      this.getSitemap('internal'),
      this.getSitemap('public'),
      this.getSitemap('private'),
    ]).then(([internal, pub, priv]) => {
      this.internalSitemaps = [internal];
      this.publicSitemap = pub;
      this.privateSitemap = priv;

      this.sitemaps.set([internal, pub, priv]);

      return this.sitemaps();
    }));
  }

  async addNode(treeType: TSitemapTreeType, parentId?: string, name = 'New Route') {
    const dashboardId = self.crypto.randomUUID();

    await this.dashboardService.createDashboard({
      baseId: dashboardId,
      displayName: name,
      published: false,
      sections: [],
      shortCode: dashboardId,
      public: treeType === 'public',
      version: 1,
    });

    const node: Partial<ISitemapNode<Client>> = {
      treeType,
      parentId,
      routeType: 'DASHBOARD',
      variants: [
        {
          dashboardUuid: dashboardId,
          lang: 'en',
          name,
          pathSegment: slugify(name),
        } as ISitemapNodeVariant<Client>,
      ],
    };

    const result = await lastValueFrom(
      this.httpClient.post<ISitemapNode<FromAPI>>(
        environment.dataServiceApi + 'sitemap/node',
        node,
      ),
    );

    await this.loadSitemaps();

    return result;
  }

  async removeNode(id: string) {
    const node = this.findNodeById(
      id,
      (this.sitemaps()?.flatMap((sitemap) => sitemap.routes) as ISitemapDeletionNode[]) ?? [],
    );
    const childCount = node?.children?.length ?? 0;

    if (childCount > 0) {
      const childLabel = childCount === 1 ? 'child page' : 'child pages';

      await lastValueFrom(
        this.matDialog
          .open(ConfirmationDialogComponent, {
            data: {
              acceptText: 'OK',
              confirmationText: 'This page cannot be deleted yet.',
              confirmationNote: `This page has ${childCount} ${childLabel}. Delete or move those child pages first.`,
              hideDecline: true,
            },
          })
          .afterClosed(),
      );

      return false;
    }

    const { confirmed } = await lastValueFrom<IConfirmDialogData>(
      this.matDialog
        .open(ConfirmationDialogComponent, {
          data: {
            acceptText: 'Delete',
            cancelText: 'Cancel',
            confirmationText: 'Are you sure you want to delete this page?',
            confirmationNote: 'This page will be permanently deleted.',
          },
        })
        .afterClosed(),
    );

    if (confirmed) {
      try {
        await lastValueFrom(
          this.httpClient.delete(environment.dataServiceApi + 'sitemap/node/' + id),
        );
        await this.loadSitemaps();
        return true;
      } catch {}
    }

    return false;
  }

  private findNodeById(
    id: string,
    nodes: ISitemapDeletionNode[],
  ): ISitemapDeletionNode | undefined {
    for (const node of nodes) {
      if (node._id === id) {
        return node;
      }

      const childNode = this.findNodeById(id, node.children ?? []);
      if (childNode) {
        return childNode;
      }
    }

    return undefined;
  }

  updateNode(id: string, update: ISitemapNodeUpdate<Client>) {
    return lastValueFrom(
      this.httpClient.patch(environment.dataServiceApi + 'sitemap/node/' + id, update),
    );
  }

  updateVariant(sitemapNodeId: string, lang: string, update: ISitemapNodeVariantUpdate<Client>) {
    return lastValueFrom(
      this.httpClient.patch(
        `${environment.dataServiceApi}sitemap/node/${sitemapNodeId}/variant/${lang}`,
        update,
      ),
    );
  }

  /** @deprecated Legacy compatibility API. Need to stop using full sitemap trees. */
  getSitemap(type: string): Promise<ISitemapTree<FromAPI>> {
    return lastValueFrom<ISitemapTree<FromAPI>>(
      this.httpClient.get<ISitemapTree<FromAPI>>(
        environment.dataServiceApi + 'sitemap/tree/' + type,
      ),
    );
  }
}
